import React from "react";
import { Clock3, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const CARD_CLASSES =
  "group flex h-full flex-col rounded-[16px] border-slate-200 bg-white p-5 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)] sm:p-6";

const StatusBadge = ({ platform, isLive }) => (
  <Badge
    variant="outline"
    data-testid={`platform-${platform.key}-status`}
    className={
      isLive
        ? "gap-1.5 border-[#a9dcde] bg-[#eef7f7] text-[11px] font-semibold text-[#0f6e73]"
        : "gap-1.5 border-[#f7d3ac] bg-[#fef3e8] text-[11px] font-semibold text-[#b4611b]"
    }
  >
    {isLive ? (
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
    ) : (
      <Clock3 className="h-3 w-3" aria-hidden="true" />
    )}
    {isLive ? platform.status || "Live" : platform.status}
  </Badge>
);

const PlatformFooter = ({ isLive }) =>
  isLive ? (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f6e73] transition-colors group-hover:text-[#0b1d3a]">
      Visit website
      <ArrowUpRight
        className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        aria-hidden="true"
      />
    </span>
  ) : (
    <span className="text-xs font-medium text-slate-500">
      A link will appear here once this platform is live
    </span>
  );

const PlatformBody = ({ platform, isLive }) => {
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-[#0b1d3a] sm:text-xl">{platform.title}</h3>
        <StatusBadge platform={platform} isLive={isLive} />
      </div>

      {platform.domain ? (
        <p
          data-testid={`platform-${platform.key}-domain`}
          className={`mt-1.5 text-sm font-medium ${
            isLive ? "text-[#0f6e73]" : "text-slate-500"
          }`}
        >
          {platform.domain}
        </p>
      ) : null}

      <p className="mt-3 text-sm leading-relaxed text-slate-600">{platform.text}</p>

      <div className="mt-5 flex-1" />
      <PlatformFooter isLive={isLive} />
    </>
  );
};

/**
 * Renders a platform as a plain card while it is being prepared, and as a
 * clickable link card as soon as the platform has a `url` in site.js.
 */
export const PlatformCard = ({ platform }) => {
  const isLive = Boolean(platform.url);

  if (!isLive) {
    return (
      <Card
        data-testid={`platform-${platform.key}-card`}
        className={`${CARD_CLASSES} border-dashed`}
      >
        <PlatformBody platform={platform} isLive={false} />
      </Card>
    );
  }

  return (
    <a
      href={platform.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`platform-${platform.key}-link`}
      className="block h-full rounded-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1aa3a8] focus-visible:ring-offset-2"
    >
      <Card
        data-testid={`platform-${platform.key}-card`}
        className={`${CARD_CLASSES} ring-1 ring-[#cfe6e7]`}
      >
        <PlatformBody platform={platform} isLive />
      </Card>
    </a>
  );
};
